import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useFeatures } from "../utils/features";
import {
  isPhoneWidth,
  phoneAppActive,
  phoneLayoutFor as layoutFor,
  activePageKeys,
  phoneParamPatch,
  readPhonePrefs,
  setPhoneAppSnapshot,
  writePhonePref,
} from "./mobileMode";

// The phone-app state for React (rules: ./mobileMode.js). One provider near the top of App.

const LAST_SWITCH_KEY = "jobq.phone.lastSwitch"; // last GET /features mobile answer seen here

function readLastSwitch() {
  try {
    const raw = window.localStorage.getItem(LAST_SWITCH_KEY);
    const v = raw ? JSON.parse(raw) : null;
    return v && v.enabled === true && Array.isArray(v.pages) ? { enabled: true, pages: v.pages } : null;
  } catch (e) {
    return null;
  }
}

function writeLastSwitch(mobile) {
  try { window.localStorage.setItem(LAST_SWITCH_KEY, JSON.stringify(mobile)); } catch (e) { /* ignore */ }
}

// Applies ?phone=preview|nopreview|desktop|auto once, then takes it out of the address bar.
function initialPrefs() {
  const patch = phoneParamPatch(window.location.search);
  if (patch) {
    Object.entries(patch).forEach(([k, v]) => writePhonePref(k, v));
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("phone");
      window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
    } catch (e) { /* the parameter stays visible; harmless */ }
  }
  return readPhonePrefs();
}

const OFF = {
  width: typeof window !== "undefined" ? window.innerWidth : 1024,
  phoneWidth: false,
  phoneApp: false,
  ready: true,
  prefs: { preview: false, forceDesktop: false },
  features: null,
  mobileFeature: { enabled: false, pages: [] },
  activePages: new Set(),
  phoneLayoutFor: () => false,
  setPreview: () => {},
  setForceDesktop: () => {},
};

const MobileModeContext = createContext(OFF);

export function MobileModeProvider({ children }) {
  const features = useFeatures();
  const [width, setWidth] = useState(() => window.innerWidth);
  const [prefs, setPrefs] = useState(initialPrefs);
  // Until GET /features answers, use the last answer this device saw, so a phone that had the
  // phone app opens straight into it and one that didn't shows today's page with no wait.
  const [lastSwitch] = useState(readLastSwitch);

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    const onStorage = (e) => {
      if (!e.key || e.key.startsWith("jobq.phone.")) setPrefs(readPhonePrefs());
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    if (features.loaded) writeLastSwitch(features.mobile);
  }, [features]);

  const mobileFeature = features.loaded ? features.mobile : (lastSwitch || features.mobile);

  const setPreview = useCallback((on) => {
    writePhonePref("preview", on);
    setPrefs(readPhonePrefs());
  }, []);
  const setForceDesktop = useCallback((on) => {
    writePhonePref("forceDesktop", on);
    setPrefs(readPhonePrefs());
  }, []);

  const value = useMemo(() => {
    const ctx = { width, mobileFeature, prefs };
    const phoneApp = phoneAppActive(ctx);
    // Mirrored for code outside React (pinPrompt, the admin PIN prompt). Set while rendering,
    // not in an effect: a child's effect (ProtectedRoute's PIN prompt) runs before this
    // provider's effects on the first render.
    setPhoneAppSnapshot({ phoneApp });
    // Known = the switch is known for this device: the API answered, this device saw an
    // answer before, or its own settings decide (preview / desktop layout / not a phone).
    const ready = features.loaded || Boolean(lastSwitch) || prefs.preview || prefs.forceDesktop || !isPhoneWidth(width);
    return {
      width,
      phoneWidth: isPhoneWidth(width),
      phoneApp,
      ready,
      prefs,
      features,
      mobileFeature,
      activePages: activePageKeys(mobileFeature, prefs),
      phoneLayoutFor: (pathname) => layoutFor(pathname, ctx),
      setPreview,
      setForceDesktop,
    };
  }, [width, mobileFeature, prefs, features, lastSwitch, setPreview, setForceDesktop]);

  return <MobileModeContext.Provider value={value}>{children}</MobileModeContext.Provider>;
}

export function useMobileMode() {
  return useContext(MobileModeContext);
}
